import React, { useEffect, useMemo, useState } from "react";
import { FaSearch, FaTimes } from "react-icons/fa";
import '../All CSS/FormMaster.css';

const BasePopup = ({
  open = false,
  title = "",
  onClose,
  columns = [],
  rows = [],
  onRowClick,
  searchValue = "",
  onSearchChange,
  showSearch = true,
  customHeader = null,
  customBody = null,
  footerContent = null,
  showFooter = true,
  pageSize = 10,
  pageSizeOptions = [
    { value: 5, label: "5" },
    { value: 10, label: "10" },
    { value: 20, label: "20" },
    { value: 50, label: "50" },
    { value: 100, label: "100" }
  ],
  emptyText = "No data found.",
  maxHeight = "450px",
  className = ""
}) => {
  const [page, setPage] = useState(0);
  const [pageSizeState, setPageSizeState] = useState(pageSize);

  useEffect(() => {
    if (open) {
      setPage(0);
      setPageSizeState(pageSize);
    }
  }, [open, pageSize, searchValue, rows.length]);

  const filteredRows = useMemo(() => {
    const normalizedSearch = (searchValue || "").toLowerCase().trim();

    if (!normalizedSearch) {
      return Array.isArray(rows) ? rows : [];
    }

    return (Array.isArray(rows) ? rows : []).filter((row) => {
      const values = Object.values(row || {}).join(" ").toLowerCase();
      return values.includes(normalizedSearch);
    });
  }, [rows, searchValue]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSizeState));

  useEffect(() => {
    if (page >= totalPages) {
      setPage(Math.max(0, totalPages - 1));
    }
  }, [page, totalPages]);

  const paginatedRows = useMemo(() => {
    const start = page * pageSizeState;
    return filteredRows.slice(start, start + pageSizeState);
  }, [filteredRows, page, pageSizeState]);

  if (!open) return null;

  const headerContent = customHeader || (
    <div className="Popup-header-left">
      {showSearch ? (
        <div className="Search-Container">
          <FaSearch className="Search-Icon" />
          <input
            type="text"
            placeholder="Search..."
            value={searchValue}
            onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
            className="Search-Input-Field"
          />
        </div>
      ) : (
        <div className="Popup-header-center" style={{ justifyContent: "center", }}>
          <strong>{title}</strong>
        </div>
      )}
    </div>
  );

  const bodyContent = customBody || (
    <div className="Base-Table-Container purchase-order-premium-popup-table-container" style={{ maxHeight }}>
      <table className="Base-Table-Content purchase-order-premium-popup-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {paginatedRows.length === 0 ? (
            <tr>
              <td colSpan={columns.length || 1} style={{ textAlign: "center", padding: "18px" }}>
                {emptyText}
              </td>
            </tr>
          ) : (
            paginatedRows.map((row, idx) => (
              <tr
                key={idx}
                onClick={() => onRowClick && onRowClick(row, idx)}
                style={{ cursor: onRowClick ? "pointer" : "default" }}
              >
                {columns.map((column) => (
                  <td key={`${column}-${idx}`}>{row[column] ?? ""}</td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  const footer = footerContent || (
    <div className="Pagination">
      <button
        type="button"
        className="Pagination-Button"
        onClick={() => setPage((prev) => Math.max(0, prev - 1))}
        disabled={page <= 0}
      >
        Previous
      </button>

      <div style={{ display: "flex", alignItems: "center", gap: 15 }}>
        <span className="Pagination-Center">
          Page {page + 1} of {totalPages || 1}
        </span>

        <select
          className="Input-Field purchase-order-premium-popup-page-size"
          value={pageSizeState}
          onChange={(e) => {
            const nextSize = Number(e.target.value);
            setPageSizeState(nextSize);
            setPage(0);
          }}
        >
          {pageSizeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <button
        type="button"
        className="Pagination-Button"
        onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
        disabled={page >= totalPages - 1}
      >
        Next
      </button>
    </div>
  );

  return (
    <div className="Popup-Container purchase-order-premium-popup" onClick={onClose}>
      <div className={`Popup-box purchase-order-premium-popup-box ${className}`} onClick={(e) => e.stopPropagation()}>
        <div className="Popup-header purchase-order-premium-popup-header">
          {headerContent}

          <div className="Popup-header-right purchase-order-premium-popup-header-right">
            <FaTimes className="popup-close" onClick={onClose} />
          </div>
        </div>

        <div className="Popup-Content purchase-order-premium-popup-content">{bodyContent}</div>

        {showFooter && (
          <div className="purchase-order-premium-popup-footer" style={{ marginTop: "10px", marginBottom: "8px" }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default BasePopup;
